/* istanbul ignore file - better tested visually */

import { TransformControls, TransformControlsProps } from '@react-three/drei';
import { invalidate, useFrame } from '@react-three/fiber';
import { useCallback, useRef, useState } from 'react';
import { Object3D, Quaternion, Vector3 } from 'three';

export type TransformMultipleControlsProps = {
  objects: Object3D[];
  mode: 'translate' | 'rotate' | 'rotate-individually';
} & Omit<TransformControlsProps, 'position' | 'object' | 'space' | 'mode'>;

function getObjectsCenter(objects: Object3D[]): Vector3 {
  return objects
    .reduce<Vector3>((out, object) => out.add(object.getWorldPosition(new Vector3())), new Vector3())
    .divideScalar(Math.max(objects.length, 1));
}

/**
 * TransformMultipleControls
 *
 * This is a wrapper around @react-three/drei's TransformControls which allows
 * multiple object to be transformed instead of just one.
 * @param objects - The Object3D's to transform.
 * @param ...rest - TransformControls args minus 'position', 'object' and 'space'.
 * @returns
 */
export function TransformMultipleControls({ objects, mode, ...rest }: TransformMultipleControlsProps) {
  const { onMouseDown, ...transformProps } = rest;

  const transformValues = useRef<{
    startValues: WeakMap<Object3D, { position: Vector3; quaternion: Quaternion }>;
    pivot: Vector3;
  }>();

  // Find the center point in between all the selected objects to render the controls at.
  const [transformPosition, setTransformPosition] = useState(getObjectsCenter(objects));
  useFrame(() => {
    if (objects) {
      setTransformPosition(getObjectsCenter(objects));
    }
  });

  const handleChange = useCallback(
    (e: any) => {
      if (e.target != null && transformValues.current != null) {
        // Get the transform deltas from the drei TransformControls
        const { offset, rotationAxis, rotationAngle } = e.target as {
          offset: Vector3;
          rotationAxis: Vector3;
          rotationAngle: number;
        };

        // Get the starting values
        const { startValues, pivot } = transformValues.current;

        // Apply the offset to each object we are dealing with
        objects.forEach((object) => {
          // Return if the start position hasn't been capture yet
          if (startValues.get(object) == null) {
            return;
          }

          const { position: startPosition, quaternion: startQuaternion } = startValues.get(object)!;

          // Revert the object to where it was at the start of this transform
          object.position.copy(startPosition);
          object.quaternion.copy(startQuaternion);

          // Deal with the object position in world space for the transform
          object.parent?.localToWorld(object.position);

          if (mode === 'translate') {
            // Apply the offset
            object.position.add(offset);
          } else if (mode === 'rotate' || mode === 'rotate-individually') {
            // The pivot point is the center of the selection
            const pivotWorldPosition = new Vector3().copy(pivot);

            if (mode === 'rotate') {
              // Move to the pivot point, rotate the object position, then move back
              object.position.sub(pivotWorldPosition);
              object.position.applyAxisAngle(rotationAxis, rotationAngle);
              object.position.add(pivotWorldPosition);
            }

            // Calculate the rotation axis (world space) in local space
            const worldQuat = object.getWorldQuaternion(new Quaternion());
            const localAxis = rotationAxis.clone().applyQuaternion(worldQuat.invert());

            // Finally rotate the mesh around it's own center point to keep the the facing correct
            const quaternion = new Quaternion().setFromAxisAngle(localAxis, rotationAngle);
            object.quaternion.multiply(quaternion);
          }

          // Revert the position back to local space
          object.parent?.worldToLocal(object.position);

          // Make sure the latest changes are applied for the next frame render
          object.updateMatrix();
          object.updateMatrixWorld();
          invalidate();
        });
      }
    },
    [mode, objects]
  );

  // Store the transforms of the objects at the start of the change
  const handleTransformStart = useCallback(
    (...args: any[]) => {
      transformValues.current = {
        startValues: new WeakMap(
          objects.map((object) => [
            object,
            { position: object.position.clone(), quaternion: object.quaternion.clone() },
          ])
        ),
        pivot: getObjectsCenter(objects),
      };

      onMouseDown?.(...args);
    },
    [objects, onMouseDown]
  );

  return objects.length ? (
    <TransformControls
      {...transformProps}
      position={transformPosition}
      mode={mode === 'translate' ? 'translate' : 'rotate'}
      onMouseDown={handleTransformStart}
      onObjectChange={handleChange}
    />
  ) : null;
}
